import type { ProductType } from '@periodica/products';
import type { AnalyticsEvent } from './types';

export type GtmProductRawData = {
  name: string | undefined;
  basePrice: number | undefined;
  type: ProductType;
  id: string | undefined;
  orderCount?: number;
  projectId?: string;
};

export type GtmProductData = {
  name: string | undefined;
  id: string;
  price: string;
  brand: string;
  category: ProductType;
  quantity?: number;
};

export type GtmOrderRawData = {
  orderId?: string;
  price: number;
  promocode: string;
  deliveryPrice: number;
  orderItems: GtmProductRawData[];
  projectIds?: string[];
};

declare global {
  interface Window {
    dataLayer: any;
  }
}

export class GtmProvider {
  private userId: number | string | null = null;

  private projectId: string | null = null;

  // declared private instance methods
  private formatProduct = ({
    name,
    basePrice,
    type,
    id,
    orderCount,
  }: GtmProductRawData): GtmProductData => ({
    name,
    id: id || '',
    price: basePrice?.toString() || '',
    brand: 'Periodica',
    category: type,
    ...(orderCount && { quantity: orderCount }),
  });

  private formatProducts = (products: GtmProductRawData[]): GtmProductData[] =>
    products.map((item) => this.formatProduct(item));

  setUser(user: { id: number | string } | null) {
    if (user) {
      this.userId = user.id;
    } else {
      this.userId = null;
    }
  }

  // для событий внутри редактора projectId передается так
  // для событий вне релактора projectId передается в data
  setProjectId(projectId: string | null) {
    if (projectId) {
      this.projectId = projectId;
    } else {
      this.projectId = null;
    }
  }

  logEvent(event: AnalyticsEvent) {
    const eventData = {
      ...(this.userId && { userid: this.userId }),
      ...(this.projectId && { project_id: this.projectId }),
      ...event.data,
    };
    try {
      window.dataLayer.push({
        event: event.name,
        ...eventData,
      });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        if (event.name !== '') {
          console.log(`Analytics event fired: '${event.name}'`);
          Object.keys(eventData ?? {}).length && console.log('data:', eventData);
          console.log('\n');
        }
      } else {
        console.warn('Something went wrong with GTM dataLayer: ', err);
      }
    }
  }

  logDetailView(data: GtmProductRawData) {
    // пустое событие нужно для плагина ecommerce
    // он хранит предыдущий стейт, и его нужно сбросить и сказать что это новый итем
    this.logEvent({ name: '', data: { ecommerce: null } });
    const eventData = {
      ecommerce: {
        detail: {
          products: [{ ...this.formatProduct(data) }],
        },
      },
    };
    this.logEvent({ name: 'detailView', data: eventData });
  }

  logAddToCart(data: GtmProductRawData) {
    this.logEvent({ name: '', data: { ecommerce: null } });
    const eventData = {
      ecommerce: {
        currencyCode: 'RUB',
        add: {
          products: [{ ...this.formatProduct(data) }],
        },
      },
      ...(data.projectId && { project_id: data.projectId }),
    };
    this.logEvent({ name: 'addToCart', data: eventData });
  }

  logViewCart(data: GtmProductRawData[]) {
    this.logEvent({ name: '', data: { ecommerce: null } });
    const eventData = {
      ecommerce: {
        checkout: {
          actionField: { step: 1 },
          products: this.formatProducts(data),
        },
      },
    };
    this.logEvent({ name: 'checkout', data: eventData });
  }

  logCheckout(data: GtmProductRawData[]) {
    this.logEvent({ name: '', data: { ecommerce: null } });
    const eventData = {
      ecommerce: {
        checkout: {
          actionField: { step: 2 },
          products: this.formatProducts(data),
        },
      },
    };
    this.logEvent({ name: 'checkout', data: eventData });
  }

  logPurchase(data: GtmOrderRawData) {
    this.logEvent({ name: '', data: { ecommerce: null } });
    const eventData = {
      ecommerce: {
        purchase: {
          actionField: {
            ...(data.orderId && { id: data.orderId }),
            revenue: data.price,
            shipping: data.deliveryPrice,
            coupon: data.promocode,
          },
          products: this.formatProducts(data.orderItems),
        },
      },
      ...(data.projectIds && { project_id: data.projectIds }),
    };

    this.logEvent({ name: 'purchase', data: eventData });
  }

  logGiftPurchase({ orderId, sum }: { orderId: string; sum: number }) {
    const eventRawData = {
      orderId,
      price: sum as number,
      deliveryPrice: 0,
      promocode: '',
      orderItems: [
        {
          name: 'Подарочный сертификат',
          id: 'gift_certificate',
          basePrice: sum as number,
          brand: 'Periodica',
          type: 'certificate' as ProductType,
          orderCount: 1,
        },
      ],
    };
    this.logPurchase(eventRawData);
  }

  logDesignPurchase({ pageCount, orderPrice }: { pageCount: number; orderPrice: number }) {
    const eventRawData = {
      price: orderPrice as number,
      deliveryPrice: 0,
      promocode: '',
      orderItems: [
        {
          name: `Услуга дизайна (${pageCount} стр.)`,
          id: `design_${pageCount}`,
          basePrice: orderPrice as number,
          brand: 'Periodica',
          type: 'design' as ProductType,
          orderCount: 1,
        },
      ],
    };
    this.logPurchase(eventRawData);
  }

  logClick(data: GtmProductRawData) {
    this.logEvent({ name: '', data: { ecommerce: null } });
    const eventData = {
      ecommerce: {
        click: {
          products: [{ ...this.formatProduct(data) }],
        },
      },
    };
    this.logEvent({ name: 'productClick', data: eventData });
  }

  logImpressions(data: GtmProductRawData) {
    this.logEvent({ name: '', data: { ecommerce: null } });
    const eventData = {
      ecommerce: {
        currencyCode: 'RUB',
        impressions: [{ ...this.formatProduct(data) }],
      },
    };
    this.logEvent({ name: 'impressions', data: eventData });
  }
}
